<template>
  <NuxtLayout name="standard">
    <UPage v-if="strapiPageData" :ui="{ root: 'relative' }">
      <ConnectifTag :name="CONNECTIF_PAGE_TAGS.OTHERS" />
      <ConnectifTag :name="CONNECTIF_CONTENT_TAGS.OTHERS" />
      <UPageHero
        :title="strapiPageData.attributes.hero.title"
        :ui="bgImageHero"
        :style="{
          backgroundImage: `url(${strapiPageData.attributes.hero.image.data.attributes.url})`,
        }"
      >
        <template #links>
          <UButton
            v-for="(item, index) in strapiPageData.attributes.hero.links"
            :key="index"
            color="primary"
            size="xl"
            :to="item.to"
            @click.prevent="handleHeroLinkClick(index, item.to)"
            >{{ item.label }}</UButton
          >
        </template>
      </UPageHero>

      <UPageSection :title="strapiPageData.attributes.info_cards.title">
        <UPageGrid>
          <UPageCard
            v-for="(card, index) in strapiPageData.attributes.info_cards.cards"
            :key="index"
            v-bind="card"
            :ui="{
              leading:
                'w-13 h-13 bg-primary-400 flex justify-center items-center rounded-full',
              leadingIcon: 'size-6 shrink-0 text-black',
            }"
          />
        </UPageGrid>
      </UPageSection>

      <UPageSection
        :title="strapiPageData.attributes.steps.title"
        :description="strapiPageData.attributes.steps.description"
        orientation="vertical"
        :ui="{
          container: 'pt-2 lg:pt-2 sm:pt-2',
        }"
      >
        <TGrid :gap-y="4">
          <div
            class="col-span-12 lg:col-span-6 flex flex-col justify-center gap-6"
          >
            <UPageFeature
              v-for="(item, index) in strapiPageData.attributes.steps.features"
              :key="index"
              v-bind="item"
              class="col-span-12 md:col-span-3"
              :ui="{ leadingIcon: 'size-12' }"
            />
          </div>
          <TLazyimg
            :src="strapiPageData.attributes.steps.image.data.attributes.url"
            alt=""
            classes-img="object-cover w-full h-full rounded-lg"
            classes="col-span-12 lg:col-span-6 mt-6 lg:mt-0 aspect-w-6 aspect-h-4"
          />
        </TGrid>
      </UPageSection>

      <UPageCTA
        v-bind="strapiPageData.attributes.cta_banner"
        variant="soft"
        :ui="{
          root: 'bg-primary-900 rounded-none',
          title: 'text-white',
          description: 'text-white',
        }"
      >
        <template #links>
          <UButton
            v-for="(item, index) in strapiPageData.attributes.cta_banner.links"
            :key="index"
            color="primary"
            size="xl"
            :to="item.to"
            @click.prevent="handleHeroCTAClick(index, item.to)"
            >{{ item.label }}</UButton
          >
        </template>
      </UPageCTA>

      <UPageSection
        v-bind="strapiPageData.attributes.faqs"
        orientation="horizontal"
      >
        <UAccordion
          :items="strapiPageData.attributes.faqs.questions"
          :unmount-on-hide="false"
          :ui="{ trigger: 'text-base', body: 'text-base text-muted' }"
        />
      </UPageSection>

      <UPageSection
        id="contact-form"
        v-bind="strapiPageData.attributes.contact_form"
        orientation="horizontal"
        :ui="{
          root: 'bg-gray-100',
          description: 'text-gray-900',
        }"
      >
        <UForm
          ref="form"
          :schema="schema"
          :state="state"
          class="space-y-3"
          @submit="onSubmit"
        >
          <UFormField name="name">
            <UInput
              v-model="state.name"
              size="lg"
              :placeholder="t('auth.register-optin.name')"
              :aria-label="t('auth.register-optin.name')"
              :title="t('auth.register-optin.name-title')"
              autocomplete="on"
            />
          </UFormField>
          <UFormField name="email">
            <UInput
              v-model="state.email"
              type="email"
              size="lg"
              :placeholder="t('auth.register-optin.email')"
              :aria-label="t('auth.register-optin.email')"
              :title="t('auth.register-optin.email-title')"
              autocomplete="on"
            />
          </UFormField>
          <UFormField required name="phone">
            <UInput
              v-model="state.phone"
              size="lg"
              type="tel"
              :placeholder="`${t('auth.register.phone')}`"
              :aria-label="`${t('auth.register.phone')}`"
              :title="t('auth.register.phone-title')"
              autocomplete="on"
            />
          </UFormField>

          <div class="flex flex-col items-center">
            <UButton
              class="mt-6 mb-2"
              size="xl"
              variant="solid"
              type="submit"
              :disabled="!isValid"
              :loading="currentState === 'SENDING'"
            >
              {{ strapiPageData.attributes.contact_form.button }}
            </UButton>
            <TText size="sm" tag="div" color="text-gray-700" classes="mt-4">
              <i18n-t
                keypath="landings.owner-campaign.terms.label"
                scope="global"
              >
                <template #useConditions>
                  <ULink
                    :active="true"
                    target="_blank"
                    :href="routes().useConditions()"
                    >{{
                      t('landings.owner-campaign.terms.useConditions')
                    }}</ULink
                  >
                </template>
                <template #privacyPolicy>
                  <ULink
                    :active="true"
                    target="_blank"
                    :href="routes().privacyPolicy()"
                    >{{
                      t('landings.owner-campaign.terms.privacyPolicy')
                    }}</ULink
                  >
                </template>
              </i18n-t>
            </TText>
          </div>
        </UForm>
      </UPageSection>

      <UAlert
        v-if="strapiPageData.attributes.alert"
        color="neutral"
        variant="outline"
        orientation="horizontal"
        :ui="{ root: 'fixed bottom-0 rounded-none' }"
      >
        <template #actions>
          <UButton
            v-for="(item, index) in strapiPageData.attributes.alert.actions"
            :key="index"
            :size="item.size"
            :variant="item.variant"
            :to="item.to"
            @click.prevent="handleAlertClick(index, item.to)"
          >
            {{ item.label }}
          </UButton>
        </template>
      </UAlert>
    </UPage>
  </NuxtLayout>
</template>

<script setup lang="ts">
// Constants
import { MenuPublicHome } from '~~/modules/menu/constants/public-menu'
import {
  CONNECTIF_PAGE_TAGS,
  CONNECTIF_CONTENT_TAGS,
} from '~~/modules/integrations/connectif/constants'

// Types
import { type schemaType as ownerBookingsCampaignPageSchemaType } from '~~/shared/api/strapi/owner-campaign/schema'

// Composables
import { useHeadInfoComposable } from '~~/modules/head/composables/useHeadInfoComposable'
import { useZodValidator } from '~~/modules/form/composables/useZodValidator'
import {
  sendGa4ImproveYourReservationsHeroClick,
  sendGa4ImproveYourReservationsCTAClick,
  sendGa4ImproveYourReservationsAlertClick,
  sendGa4ImproveYourReservationsFormSubmit,
} from '~~/modules/integrations/ga4/composables/useAnalyticsGa4Composable'
import { sendConnectifPage } from '~~/modules/integrations/connectif/composables/useConnectifComposable'

// Apis
import { browserFetch as sendEmail } from '~~/modules/api/internal/contact-form'
import { paramsSchema } from '~~/shared/api/internal/contact-form/paramsSchema'
import {
  serverFetch as getPageData,
  type returnType as pageDataType,
} from '~~/modules/api/strapi/owner-campaign'

// Components UI Config
import { bgImageHero } from '~~/modules/design-system/utils/nuxt-ui-config/components/page-hero'

definePageMeta({
  publicMenuItem: MenuPublicHome,
})

const { setHeadInfo } = useHeadInfoComposable()
const { validatePhone } = useZodValidator()

const showErrorPage = (error: ApiResponseError) => {
  throw createError({
    statusCode: 503,
    message: error.message,
    fatal: true,
  })
}

const rawStrapiPageData: pageDataType = await getPageData()

let strapiPageData: ownerBookingsCampaignPageSchemaType

if (rawStrapiPageData.success && rawStrapiPageData.success.data) {
  strapiPageData = rawStrapiPageData.success.data

  if (strapiPageData) {
    setHeadInfo({
      title: strapiPageData.attributes.metadata.title,
      description: strapiPageData.attributes.metadata.description,
      images: [
        {
          url: strapiPageData.attributes.metadata.image.data.attributes.url,
          alt: '',
        },
      ],
      robotsVisible: true,
    })
  }
  sendConnectifPage()
} else if (rawStrapiPageData.error) {
  showErrorPage(rawStrapiPageData.error as ApiResponseError)
}

const states = {
  IDLE: 'IDLE',
  SENDING: 'SENDING',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
}

const state = reactive({
  name: undefined,
  phone: undefined,
  email: undefined,
})

const schema = paramsSchema.extend({
  phone: validatePhone(),
})

const currentState: Ref<keyof typeof states> = ref('IDLE')

const isValid = computed(() => {
  return schema.safeParse(state).success
})

const handleHeroLinkClick = (index: number, to: string) => {
  sendGa4ImproveYourReservationsHeroClick({
    index,
    eventCallback: () => {
      navigateTo(to, { external: true })
    },
  })
}

const handleHeroCTAClick = (index: number, to: string) => {
  sendGa4ImproveYourReservationsCTAClick({
    index,
    eventCallback: () => {
      navigateTo(to, { external: true })
    },
  })
}

const handleAlertClick = (index: number, to: string) => {
  sendGa4ImproveYourReservationsAlertClick({
    index,
    eventCallback: () => {
      navigateTo(to, { external: true })
    },
  })
}

async function onSubmit() {
  const result = schema.safeParse(state)

  if (result.success) {
    currentState.value = 'SENDING'

    const parsedData = result.data

    const sendNewEmail = await sendEmail(parsedData)

    if (sendNewEmail.success && sendNewEmail.success.data) {
      sendGa4ImproveYourReservationsFormSubmit()
      currentState.value = 'SUCCESS'
    } else {
      currentState.value = 'ERROR'
    }
  }
}
</script>
